import React, { useEffect, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import Icon from "@mui/material/Icon";
import InputAdornment from "@mui/material/InputAdornment";
import TextField from "@mui/material/TextField";
import FormLabel from "@mui/material/FormLabel";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { useAuthenticator } from "@aws-amplify/ui-react";
import { call } from "services/axiosService/core";
import { useMaterialUIController, setProfile, setPersona } from "context";
import { useNavigate } from "react-router-dom";
import palette from "assets/theme/base/palette";
import PropTypes from "prop-types";
import * as Sentry from "@sentry/browser";
import { useTranslation } from "react-i18next";

const STEPS = 3;

const StepDots = ({ current, total }) => (
  <MDBox display="flex" justifyContent="center" gap={1} mb={3}>
    {Array.from({ length: total }).map((_, i) => (
      <MDBox
        key={i}
        sx={{
          width: i === current ? 24 : 8,
          height: 8,
          borderRadius: 4,
          bgcolor: i === current ? palette.brand[500] : palette.brand[100],
          transition: "all 300ms ease",
        }}
      />
    ))}
  </MDBox>
);

const ScreenHeader = ({ icon, title, subtitle }) => (
  <MDBox textAlign="center" mb={3}>
    <MDBox
      sx={{
        width: 72,
        height: 72,
        borderRadius: "50%",
        bgcolor: palette.brand[50],
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        mx: "auto",
        mb: 2,
      }}
    >
      <Icon sx={{ fontSize: "36px !important", color: palette.brand[500] }}>{icon}</Icon>
    </MDBox>
    <MDTypography variant="h5" fontWeight="bold" mb={0.5}>
      {title}
    </MDTypography>
    {subtitle && (
      <MDTypography variant="body2" color="secondary" lineHeight={1.6}>
        {subtitle}
      </MDTypography>
    )}
  </MDBox>
);

const AthleteOnBoardingScreen = ({ role, setOpenLoader }) => {
  const { user, signOut } = useAuthenticator((context) => [context.user]);
  const [open, setOpen] = useState(false);
  const [showError, setShowError] = useState(false);
  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("");
  const [personaName, setPersonaName] = useState([]);
  const [screen, setScreen] = useState(0);
  const [controller, dispatch] = useMaterialUIController();
  const [parentName, setParentName] = useState();
  const { profile } = controller;
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const logOutHandler = () => {
    localStorage.removeItem("session");
    signOut();
  };

  const completeHandler = () => {
    window.pendo.track("Learner Onboarding Completed");
    let type = [];
    let payment_status = profile.payment_status;
    type = profile.type;
    const data = {
      pk: profile.pk ?? "user",
      sk: profile.sk ?? `user[${profile.id}]`,
      id: profile.id ?? null,
      cognito_user_id: profile.cognito_user_id ?? user.getUsername(),
      email: profile.email ?? null,
      status: "active",
      signup_at: new Date(),
      phone: phone != "" ? phone : null,
      relationships: profile.relationships,
      true_mindsets: profile.true_mindsets,
      subscription_type: profile.subscription_type ?? null,
      auto_renew: true,
      true_mindsets_id: null,
      payment_status: payment_status,
      type: type,
      details: {
        name: profile.details.name,
        dob: profile.details.dob,
        persona: profile.details.persona ?? personaName,
        gender: profile.details.gender,
        address: {
          street: profile.details.address.street,
          city: profile.details.address.city,
          state: profile.details.address.state,
          country: profile.details.address.country,
          zip: profile.details.address.zip,
        },
      },
      identities: [
        {
          google: { id: profile.identities[0].google.id ?? null },
          facebook: { id: profile.identities[0].facebook.id ?? null },
          apple: { id: profile.identities[0].apple.id ?? null },
          amazon: { id: profile.identities[0].amazon.id ?? null },
          email: { id: profile.cognito_user_id ?? user.getUsername() },
        },
      ],
    };

    call(`users/${profile.id}`, "PUT", { body: data })
      .then((res) => {
        if (res.status == 200) {
          handleClose();
          let details = res.data.ModifiedAttributes;
          details.id = res.data.Id;
          setProfile(dispatch, details);
          const usertype = details.type;
          if (usertype.length == 1) {
            usertype[0].toLowerCase() === "learner" && setPersona(dispatch, "athlete");
            usertype[0].toLowerCase() === "adult" && setPersona(dispatch, "guardian");
          } else if (type.length == 2) {
            usertype[0].toLowerCase() === "adult" && setPersona(dispatch, "guardian");
            usertype[0].toLowerCase() === "learner" && setPersona(dispatch, "adultAthlete");
          } else {
            setPersona(dispatch, "guardian");
          }
          setOpenLoader(true);
        } else if (res.status == 400) {
          setShowError(true);
        } else if (res.status == 440) {
          handleClose();
        }
      })
      .catch((err) => {
        setShowError(true);
        Sentry.captureException(err);
      });
  };

  useEffect(() => {
    if (profile.payment_status === "pending" || profile.payment_status === "fail") {
      if (profile.type.length === 1) {
        handleOpen();
        setScreen(3);
      } else {
        handleOpen();
        setScreen(4);
      }
    } else if (profile.status.toLowerCase() === "inactive") {
      call(`users/${profile.id}`, "GET", { params: { type: "guardian" } })
        .then((res) => {
          if (res.status === 200) {
            setFullName(profile.details.name);
            setParentName(res.data[0].details.name);
          }
        })
        .catch((err) => Sentry.captureException(err));
      handleOpen();
    }
  }, []);

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: 3, overflowX: "hidden" } }}
    >
      {/* Gradient header bar */}
      <MDBox
        sx={{
          background: `linear-gradient(135deg, ${palette.brand[400]} 0%, ${palette.brand[700]} 100%)`,
          py: 2.5,
          px: 3,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <MDTypography variant="h5" fontWeight="bold" sx={{ color: "#fff" }}>
          {t("messages.onboarding.welcome1").split("!")[0]}
        </MDTypography>
      </MDBox>

      <DialogContent sx={{ px: { xs: 3, sm: 5 }, py: 4 }}>

        {/* Screen 0 — Welcome */}
        {screen === 0 && (
          <MDBox>
            <ScreenHeader
              icon="waving_hand"
              title={t("messages.onboarding.welcome1")}
              subtitle={t("messages.onboarding.welcome2")}
            />
            <StepDots current={0} total={STEPS} />
            <MDButton
              variant="contained"
              color="info"
              fullWidth
              onClick={() => setScreen(1)}
              sx={{ borderRadius: 2, py: 1.5 }}
              endIcon={<Icon>arrow_forward</Icon>}
            >
              {t("buttonTitles.next")}
            </MDButton>
          </MDBox>
        )}

        {/* Screen 1 — Personal Info */}
        {screen === 1 && (
          <MDBox component="form" onSubmit={(e) => { e.preventDefault(); setScreen(2); }}>
            <ScreenHeader
              icon="person"
              title={t("headings.personalInfo")}
              subtitle={t("formLabels.fillInDetails") || "Please confirm your details below"}
            />
            <StepDots current={1} total={STEPS} />

            <MDBox mb={2}>
              <FormLabel sx={{ fontSize: "0.82rem", fontWeight: 600, mb: 0.5, display: "block" }}>
                {profile.relationships?.parents ? t("formLabels.parent") : t("formLabels.guardian")}
              </FormLabel>
              <TextField value={parentName} fullWidth required disabled size="small" />
            </MDBox>

            <MDBox mb={2}>
              <FormLabel sx={{ fontSize: "0.82rem", fontWeight: 600, mb: 0.5, display: "block" }}>
                {t("formLabels.yourName")}
              </FormLabel>
              <TextField value={fullName} fullWidth required disabled size="small" />
            </MDBox>

            <MDBox mb={3}>
              <FormLabel sx={{ fontSize: "0.82rem", fontWeight: 600, mb: 0.5, display: "block" }}>
                {t("formLabels.phoneNumber")}
              </FormLabel>
              <TextField
                type="text"
                size="small"
                inputProps={{ maxLength: 10 }}
                value={phone}
                error={phone.length < 10 && phone.length > 0}
                helperText={phone.length < 10 && phone.length > 0 ? "Enter a valid 10-digit number" : ""}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <MDTypography variant="body2" fontWeight="medium">+1</MDTypography>
                    </InputAdornment>
                  ),
                }}
                fullWidth
                onChange={(e) => {
                  const re = /^[0-9\b]+$/;
                  if (e.target.value === "" || re.test(e.target.value)) setPhone(e.target.value);
                }}
              />
            </MDBox>

            {showError && (
              <MDTypography variant="caption" color="error" display="block" textAlign="center" mb={2}>
                {t("messages.onboarding.error")}
              </MDTypography>
            )}

            <MDBox display="flex" gap={2}>
              <MDButton
                variant="outlined"
                color="secondary"
                fullWidth
                onClick={() => setScreen(0)}
                sx={{ borderRadius: 2 }}
                startIcon={<Icon>arrow_back</Icon>}
              >
                {t("buttonTitles.previous")}
              </MDButton>
              <MDButton
                variant="contained"
                color="info"
                fullWidth
                type="submit"
                disabled={phone.length < 10 && phone.length > 0}
                sx={{ borderRadius: 2 }}
                endIcon={<Icon>arrow_forward</Icon>}
              >
                {t("buttonTitles.next")}
              </MDButton>
            </MDBox>
          </MDBox>
        )}

        {/* Screen 2 — Done */}
        {screen === 2 && (
          <MDBox>
            <ScreenHeader
              icon="check_circle"
              title={t("buttonTitles.finish") || "All Set!"}
              subtitle={t("messages.onboarding.completedChild")}
            />
            <StepDots current={2} total={STEPS} />
            <MDBox display="flex" gap={2}>
              <MDButton
                variant="outlined"
                color="secondary"
                fullWidth
                onClick={() => setScreen(1)}
                sx={{ borderRadius: 2 }}
                startIcon={<Icon>arrow_back</Icon>}
              >
                {t("buttonTitles.previous")}
              </MDButton>
              <MDButton
                variant="contained"
                color="info"
                fullWidth
                onClick={completeHandler}
                sx={{ borderRadius: 2 }}
                endIcon={<Icon>rocket_launch</Icon>}
              >
                {t("buttonTitles.finish")}
              </MDButton>
            </MDBox>
          </MDBox>
        )}

        {/* Screen 3 — Payment issue (child) */}
        {screen === 3 && (
          <MDBox>
            <ScreenHeader
              icon="error_outline"
              title={t("messages.onboarding.paymentIssueTitle")}
              subtitle={t("messages.onboarding.paymentIssueMessageChild")}
            />
            <MDBox
              sx={{
                bgcolor: "#fff3e0",
                borderRadius: 2,
                p: 2,
                mb: 3,
                display: "flex",
                alignItems: "center",
                gap: 1.5,
              }}
            >
              <Icon sx={{ color: "#e65100", fontSize: 20 }}>mail_outline</Icon>
              <MDTypography variant="body2" color="text">
                {t("messages.onboarding.contactSupport")}{" "}
                <a href="mailto:support@maxu.co" style={{ color: palette.brand[500], fontWeight: 600 }}>
                  support@maxu.co
                </a>
              </MDTypography>
            </MDBox>
            <MDButton
              variant="contained"
              color="error"
              fullWidth
              onClick={logOutHandler}
              sx={{ borderRadius: 2 }}
              startIcon={<Icon>logout</Icon>}
            >
              {t("buttonTitles.logOut")}
            </MDButton>
          </MDBox>
        )}

        {/* Screen 4 — Payment issue (adult) */}
        {screen === 4 && (
          <MDBox>
            <ScreenHeader
              icon="payment"
              title={t("messages.onboarding.paymentIssueTitle")}
              subtitle={t("messages.onboarding.paymentIssueMessage")}
            />
            <MDBox
              sx={{
                bgcolor: "#fff3e0",
                borderRadius: 2,
                p: 2,
                mb: 3,
                display: "flex",
                alignItems: "center",
                gap: 1.5,
              }}
            >
              <Icon sx={{ color: "#e65100", fontSize: 20 }}>mail_outline</Icon>
              <MDTypography variant="body2" color="text">
                {t("messages.onboarding.contactSupport")}{" "}
                <a href="mailto:support@maxu.co" style={{ color: palette.brand[500], fontWeight: 600 }}>
                  support@maxu.co
                </a>
              </MDTypography>
            </MDBox>
            <MDButton
              variant="contained"
              color="info"
              fullWidth
              onClick={() => navigate("/profile")}
              sx={{ borderRadius: 2 }}
              startIcon={<Icon>account_circle</Icon>}
            >
              {t("buttonTitles.profile")}
            </MDButton>
          </MDBox>
        )}

      </DialogContent>
    </Dialog>
  );
};

AthleteOnBoardingScreen.defaultProps = {
  role: "athlete",
  setOpenLoader: () => {},
};

AthleteOnBoardingScreen.propTypes = {
  role: PropTypes.string,
  setOpenLoader: PropTypes.func,
};

export default AthleteOnBoardingScreen;
